import React from "react";
import {
  Avatar,
  Button,
  Chip,
  Typography
} from "@material-ui/core";
import {
  AccessTime,
  CheckCircle,
  Cancel
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

const useStyles = makeStyles(theme => ({
  card: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    padding: theme.spacing(1.4),
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#FFFFFF",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
    transition: "transform .18s ease, box-shadow .18s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 32px rgba(15,23,42,.10)"
    }
  },

  clickable: {
    cursor: "pointer"
  },

  top: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0
  },

  avatar: {
    width: 40,
    height: 40,
    borderRadius: 13,
    flexShrink: 0,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    fontWeight: 900,
    fontSize: ".8rem"
  },

  name: {
    fontWeight: 900,
    fontSize: ".88rem",
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  company: {
    fontSize: ".7rem",
    color: theme.palette.text.secondary,
    marginTop: 3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  details: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 5,
    marginTop: 12,
    padding: "10px 11px",
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "rgba(148,163,184,.08)",
    border: "1px solid rgba(148,163,184,.14)"
  },

  detailText: {
    fontSize: ".7rem",
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  conversation: {
    fontSize: ".7rem",
    fontWeight: 800
  },

  value: {
    fontWeight: 900,
    fontSize: "1.05rem",
    color: "#0066FF",
    marginTop: 12
  },

  progressTrack: {
    height: 7,
    background: "#E2E8F0",
    borderRadius: 999,
    overflow: "hidden",
    marginTop: 9
  },

  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg,#0066FF,#00A3FF)"
  },

  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginTop: 9
  },

  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(.8),
    marginTop: 11
  },

  detailsBtn: {
    borderRadius: 10,
    fontWeight: 800,
    fontSize: ".72rem",
    textTransform: "none",
    color: "#0066FF",
    border: "1px solid rgba(0,102,255,.24)"
  },

  deleteBtn: {
    borderRadius: 10,
    fontWeight: 800,
    fontSize: ".72rem",
    textTransform: "none",
    color: "#EF4444",
    border: "1px solid rgba(239,68,68,.24)"
  }
}));

const statusConfig = {
  open: {
    label: "Em andamento",
    color: "#B8860B",
    background: "rgba(255,200,87,.12)",
    icon: <AccessTime style={{ fontSize: 14 }} />
  },
  won: {
    label: "Ganho",
    color: "#16A34A",
    background: "rgba(22,163,74,.10)",
    icon: <CheckCircle style={{ fontSize: 14 }} />
  },
  lost: {
    label: "Perdido",
    color: "#EF4444",
    background: "rgba(239,68,68,.10)",
    icon: <Cancel style={{ fontSize: 14 }} />
  }
};

const OpportunityCard = ({
  opportunity,
  formatCurrency,
  onOpen,
  onDelete
}) => {
  const classes = useStyles();

  const status =
    statusConfig[opportunity.status] ||
    statusConfig.open;

  const name =
    opportunity.contact?.name ||
    opportunity.ticket?.contact?.name ||
    opportunity.title ||
    "Nova oportunidade";

  const initials = String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

  const companyName =
    opportunity.title?.includes(" — ")
      ? opportunity.title
          .split(" — ")
          .slice(1)
          .join(" — ")
      : opportunity.product || "Empresa não informada";

  return (
    <div className={classes.card}>
      <div
        className={classes.clickable}
        onClick={() => onOpen(opportunity)}
      >
        <div className={classes.top}>
          <div className={classes.nameRow}>
            <Avatar className={classes.avatar}>
              {initials}
            </Avatar>

            <div style={{ minWidth: 0 }}>
              <Typography className={classes.name}>
                {name}
              </Typography>

              <Typography className={classes.company}>
                {companyName}
              </Typography>
            </div>
          </div>

          <Chip
            size="small"
            icon={status.icon}
            label={
              opportunity.stage?.name ||
              status.label
            }
            style={{
              maxWidth: 140,
              height: 25,
              fontWeight: 900,
              fontSize: ".65rem",
              background:
                opportunity.stage?.color
                  ? `${opportunity.stage.color}18`
                  : status.background,
              color:
                opportunity.stage?.color ||
                status.color,
              border: `1px solid ${
                opportunity.stage?.color ||
                status.color
              }44`
            }}
          />
        </div>

        <div className={classes.details}>
          <Typography className={classes.detailText}>
            🌐 <strong>Origem:</strong>{" "}
            {opportunity.source || "Manual"}
          </Typography>

          <Typography className={classes.detailText}>
            📞 <strong>Telefone:</strong>{" "}
            {opportunity.contact?.number ||
              "Não informado"}
          </Typography>

          <Typography className={classes.detailText}>
            ✉️ <strong>E-mail:</strong>{" "}
            {opportunity.contact?.email ||
              "Não informado"}
          </Typography>

          <Typography className={classes.detailText}>
            👤 <strong>Responsável:</strong>{" "}
            {opportunity.user?.name ||
              "Não atribuído"}
          </Typography>

          <Typography className={classes.detailText}>
            📅 <strong>Entrada:</strong>{" "}
            {opportunity.createdAt
              ? moment(opportunity.createdAt).format(
                  "DD/MM/YYYY [às] HH:mm"
                )
              : "Não informada"}
          </Typography>

          <Typography
            className={classes.conversation}
            style={{
              color: opportunity.ticketId
                ? "#16A34A"
                : "#D97706"
            }}
          >
            {opportunity.ticketId
              ? "💬 Conversa vinculada"
              : "⏳ Conversa ainda não iniciada"}
          </Typography>
        </div>

        <Typography className={classes.value}>
          {formatCurrency(opportunity.value)}
        </Typography>

        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{
              width: `${Number(
                opportunity.probability || 0
              )}%`
            }}
          />
        </div>

        <div className={classes.chips}>
          <Chip
            size="small"
            label={`${opportunity.probability || 0}% prob.`}
            style={{
              height: 23,
              fontWeight: 900,
              fontSize: ".65rem",
              background: "rgba(0,102,255,.08)",
              color: "#0066FF"
            }}
          />

          <Chip
            size="small"
            label={
              opportunity.expectedCloseDate
                ? moment(
                    opportunity.expectedCloseDate
                  ).format("DD/MM/YYYY")
                : "Sem previsão"
            }
            style={{
              height: 23,
              fontWeight: 900,
              fontSize: ".65rem",
              background: "#F1F5F9",
              color: "#64748B"
            }}
          />
        </div>
      </div>

      <div className={classes.actions}>
        <Button
          size="small"
          variant="outlined"
          className={classes.detailsBtn}
          onClick={() => onOpen(opportunity)}
        >
          Ver detalhes
        </Button>

        <Button
          size="small"
          variant="outlined"
          className={classes.deleteBtn}
          onClick={() => onDelete(opportunity)}
        >
          Excluir
        </Button>
      </div>
    </div>
  );
};

export default OpportunityCard;
